import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// import BusinessCardSettings from "./BusinessCardSettings";
import AccountInformation from "../../components/profile/AccountInformation";
import MyTransferPoints from "../../components/profile/MyTransferPoints";
// import Settings from "../../components/profile/Settings";
import { userService } from "../../services/api";

const ClubAdminProfile = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get active tab from URL or default to account-information for club admins
  const activeTab = searchParams.get("tab") || "account-information";

  // Club Admin Profile API state
  const [clubProfileData, setClubProfileData] = useState(null);
  const [formApiData, setFormApiData] = useState({
    name: "",
    mobile: "",
    email: "",
    club_name: "",
    club_logo: null,
    first_name: "",
    last_name: "",
    admin_position: "",
    license_number: "",
    club_document: null,
    address: "",
    city: "",
    country: "",
    zipcode: "",
    club_id: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [pendingLogoFile, setPendingLogoFile] = useState(null);


  // Map API data to form fields
  const mapApiToForm = (data) => ({
    name: data.name || "",
    mobile: data.mobile || "",
    email: data.email || "",
    club_name: data.club_details?.name || "",
    club_logo: data.club_details?.logo || null,
    first_name: data.first_name || "",
    last_name: data.last_name || "",
    // Prefer explicit user_details.role_type (selected position) if present
    admin_position: (data.user_details?.role_type || data.club_details?.contact_person_position || ""),
    license_number: data.club_details?.license_number || "",
    club_document: data.document_url || null,
    address: data.address || "",
    city: data.city || "",
    country: data.country || "",
    zipcode: data.zipcode || "",
    club_id: data.club_details?.wyscout_id || "",
  });

  // Map form fields to API payload
  const mapFormToApi = (form) => {
    // Create FormData for multipart/form-data submission
    const formData = new FormData();

    // Club can only update these fields:
    // - license_number
    // - first_name
    // - last_name
    // - admin_position (role_type)

    if (form.license_number)
      formData.append("license_number", form.license_number);
    if (form.first_name) formData.append("first_name", form.first_name);
    if (form.last_name) formData.append("last_name", form.last_name);
    if (form.admin_position) formData.append("role_type", form.admin_position);

    // Commented out - Club cannot update these fields:
    // if (form.mobile) formData.append("mobile", form.mobile);
    // if (form.name) formData.append("name", form.name);
    // if (form.address) formData.append("address", form.address);
    // if (form.city) formData.append("city", form.city);
    // if (form.country) formData.append("country", form.country);
    // if (form.zipcode) formData.append("zipcode", form.zipcode);
    // if (form.club_name) formData.append("club_name", form.club_name);
    // if (form.email) formData.append("email", form.email);

    // Note: Club logo is now handled separately via uploadClubLogo API

    return formData;
  };

  // Fetch club admin profile on mount
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError("");

    userService
      .getClubAdminProfile()
      .then((result) => {
        if (isMounted) {
          if (result.success) {
            setClubProfileData(result.data);
            console.log("result", result.data);
            setFormApiData(mapApiToForm(result.data));
          } else {
            setError(result.error);
          }
        }
      })
      .catch((err) => {
        console.log(err);
        if (isMounted) {
          setError("Failed to load profile");
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Handle API-editable field changes
  const handleApiInputChange = (field, value) => {
    // Only allow updating specific fields for club admin
    const allowedFields = [
      "license_number",
      "first_name",
      "last_name",
      "admin_position",
    ];
    if (allowedFields.includes(field)) {
      setFormApiData((prev) => ({ ...prev, [field]: value }));
    }
  };

  // Commented out - Club cannot update documents
  // Handle file upload for club document
  const handleClubDocumentChange = (file) => {
    // Club admin cannot update documents - function disabled
    console.log("Club document change blocked - not allowed for club admin");
  };

  // Handle club logo upload
  const handleClubLogoChange = (file) => {
    if (!file) return;

    // Client-side validation
    if (file.size > 5 * 1024 * 1024) {
      setError("File size exceeds maximum allowed size of 5MB.");
      return;
    }

    setError("");
    setPendingLogoFile(file);
    // Show preview immediately; actual upload happens when clicking "Save changes"
    setFormApiData((prev) => ({
      ...prev,
      club_logo: file,
    }));
  };

  const handleMobileUpdate = (newMobile) => {
    setFormApiData((prev) => ({ ...prev, mobile: newMobile }));
    setClubProfileData((prev) =>
      prev ? { ...prev, mobile: newMobile } : prev
    );
  };

  // Save changes to API
  const handleSave = async () => {
    setIsSaving(true);
    setError("");

    try {
      // 1) Upload pending logo first (if any)
      if (pendingLogoFile) {
        const logoFormData = new FormData();
        logoFormData.append("logo", pendingLogoFile);

        const uploadResult = await userService.uploadClubLogo(logoFormData);

        if (!uploadResult.success) {
          setError(uploadResult.error || "Failed to upload logo");
          return false;
        }

        // Update local state with saved logo URL
        setFormApiData((prev) => ({
          ...prev,
          club_logo: uploadResult.data.logo_url,
        }));
        setClubProfileData((prev) => ({
          ...prev,
          club_details: {
            ...prev?.club_details,
            logo: uploadResult.data.logo_url,
          },
        }));
      }

      // 2) Save profile details
      const payload = mapFormToApi(formApiData);

      // Log all form data before saving
      console.log("=== SAVING CLUB ADMIN PROFILE ===");
      console.log(
        "Only allowed to update: license_number, first_name, last_name, admin_position"
      );
      console.log("Form Data:", {
        license_number: formApiData.license_number,
        first_name: formApiData.first_name,
        last_name: formApiData.last_name,
        admin_position: formApiData.admin_position,
      });
      console.log("API Payload:", payload);
      console.log("Original Profile Data:", clubProfileData);

      const result = await userService.updateClubAdminProfile(payload);

      if (result.success) {
        console.log("Save successful:", result.data);
        setClubProfileData(result.data);
        setFormApiData(mapApiToForm(result.data));
        setPendingLogoFile(null);
        setIsEditing(false);
      } else {
        console.error("Save failed:", result.error);
        setError(result.error);
      }
    } catch (err) {
      console.error("Save error:", err);
      setError("Failed to save changes");
    } finally {
      setIsSaving(false);
    }

    return true; // Return success for promise chaining
  };

  // Cancel editing and revert changes
  const handleCancel = () => {
    if (clubProfileData) {
      setFormApiData(mapApiToForm(clubProfileData));
    }
    setPendingLogoFile(null);
    setIsEditing(false);
    setError("");
  };

  // Update URL when tab changes
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  const tabs = [
    { id: "account-information", label: "Account Information" },
    { id: "my-transferpoints", label: "My TransferPoints" },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "account-information":
        return (
          <AccountInformation
            clubProfileData={clubProfileData}
            formApiData={formApiData}
            isEditing={isEditing}
            isLoading={isLoading}
            isSaving={isSaving}
            error={error}
            onApiInputChange={handleApiInputChange}
            onClubDocumentChange={handleClubDocumentChange}
            onClubLogoChange={handleClubLogoChange}
            onSave={handleSave}
            onCancel={handleCancel}
            onEdit={() => setIsEditing(true)}
            onMobileUpdate={handleMobileUpdate}
          />
        );
      case "my-transferpoints":
        return <MyTransferPoints />;
      default:
        return (
          <AccountInformation
            clubProfileData={clubProfileData}
            formApiData={formApiData}
            isEditing={isEditing}
            isLoading={isLoading}
            isSaving={isSaving}
            error={error}
            onApiInputChange={handleApiInputChange}
            onClubDocumentChange={handleClubDocumentChange}
            onClubLogoChange={handleClubLogoChange}
            onSave={handleSave}
            onCancel={handleCancel}
            onEdit={() => setIsEditing(true)}
            onMobileUpdate={handleMobileUpdate}
          />
        );
    }
  };

  const navigate = useNavigate()

  return (
    <div className="min-h-screen overflow-x-hidden bg-gray-50 ">
      <div className=" mx-auto ">
        <div className="bg-white py-4 border-b border-[#E5E6E8]">
          <div className="sm:px-14 px-4 flex items-center justify-between">
            <h1 className="text-[20px] font-bold text-gray-900">My Profile</h1>
            <button onClick={() => navigate(`/search/club/${formApiData.club_id}`)} className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg">
              Preview public profile
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white py-4 flex items-center">
          <div className="sm:px-14 px-4">
            <nav className=" flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-4 font-medium text-sm rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? "bg-[#F3F4F6] text-[#0B0B0B]"
                      : "text-[#1E1E1ECC] hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-[#F8F9FB] rounded-lg shadow-sm">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ClubAdminProfile;
